import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../Components/SEO';
import SEOPageLayout from '../../Components/SEOPageLayout';

export default function About() {
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "CheckScript AI",
    "url": "https://checkscript.online",
    "logo": "https://checkscript.online/logo512.png",
    "description": "Creators of the CheckScript AI Assessment Assistant and Neural Driver platforms.",
    "foundingDate": "2023",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "value": "10"
    }
  };

  return (
    <SEOPageLayout>
      <SEO
        title="About CheckScript — AI-Powered Assessment & Study Platform"
        description="Learn about CheckScript AI's mission to democratize access to AI-powered education tools. Discover our technology, history, and the team behind the platform."
        keywords="CheckScript, about CheckScript, AI study platform, CheckScript team, CheckScript history"
        path="/about"
        structuredData={orgSchema}
      />

      <section className="seo-hero seo-animate">
        <div className="seo-mesh-bg"></div>
        <div className="seo-container seo-container--sm">
          <div className="seo-hero-badge">🏢 About Us</div>
          <h1 className="seo-h1">
            Democratizing Access to <span className="seo-gradient-text">AI Education Tools</span>
          </h1>
          <p className="seo-lead">
            We build advanced, unrestrictive software that empowers students and developers to learn, debug, and collaborate without boundaries.
          </p>
        </div>
      </section>

      <section className="seo-section seo-section--alt seo-animate">
        <div className="seo-container">
          <div className="seo-bento-grid" style={{ gap: '64px', alignItems: 'center' }}>
            <div>
              <h2 className="seo-h2">Our Mission</h2>
              <p className="seo-text-secondary seo-mb-24" style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                At CheckScript AI, we believe that artificial intelligence should be an accessible companion for everyone, not just a cloud service controlled by large corporations. 
              </p>
              <p className="seo-text-secondary seo-mb-24" style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                Our goal is to provide a seamless bridge between complex assessment environments and the powerful capabilities of modern AI. Whether you're a student preparing for a critical exam or a developer debugging legacy code, CheckScript provides the intelligent interface you need.
              </p>
            </div>
            <div className="seo-glass-card">
              <h3 className="seo-h3 seo-mb-24">By The Numbers</h3>
              <div className="seo-bento-grid" style={{ gap: '24px' }}>
                <div>
                  <div className="seo-h2 seo-gradient-text">v19.0</div>
                  <div className="seo-text-secondary">Current Version</div>
                </div>
                <div>
                  <div className="seo-h2 seo-gradient-text">2.5k+</div>
                  <div className="seo-text-secondary">Active Users</div>
                </div>
                <div>
                  <div className="seo-h2 seo-gradient-text">15+</div>
                  <div className="seo-text-secondary">Supported Languages</div>
                </div>
                <div>
                  <div className="seo-h2 seo-gradient-text">5</div>
                  <div className="seo-text-secondary">Capture Engines</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="seo-section seo-animate">
        <div className="seo-container seo-container--sm">
          <h2 className="seo-h2 seo-text-center seo-mb-48">Our Technology Stack</h2>
          <div className="seo-bento-box seo-bento-square seo-mb-24">
            <h3 className="seo-h3">The Native Engine (Hardware-accelerated capture)</h3>
            <p className="seo-text-secondary">
              The core CheckScript host is written in optimized, Hardware-accelerated capture for Windows. It utilizes low-level APIs (Hardware-accelerated capture, Interception) to provide high-performance screen capture, instant OCR parsing, and hardware-accelerated video encoding with minimal system overhead.
            </p>
          </div>
          <div className="seo-bento-box seo-bento-square seo-mb-24">
            <h3 className="seo-h3">The Web Platform (React & Node)</h3>
            <p className="seo-text-secondary">
              Our web infrastructure is built on React and Secure backend infrastructure, providing a lightning-fast dashboard, real-time analytics, and our signature browser-based remote desktop viewer.
            </p>
          </div>
          <div className="seo-bento-box seo-bento-square">
            <h3 className="seo-h3">Edge Network & AI Infrastructure</h3>
            <p className="seo-text-secondary">
              We leverage Cloudflare's edge network for ultra-low latency Ultra-low latency streaming protocol routing. Our AI backends combine robust cloud inference (Enterprise-grade routing) with the groundbreaking Neural Driver, allowing users to run complex models (like Qwen2-VL) entirely locally on their GPUs.
            </p>
          </div>
        </div>
      </section>

      <section className="seo-cta seo-animate">
        <div className="seo-container">
          <div className="seo-cta-box">
            <h2 className="seo-h2">Join the CheckScript Ecosystem</h2>
            <p className="seo-text-secondary seo-mb-32">Experience the future of AI-assisted learning and development.</p>
            <div className="seo-hero-actions">
              <Link to="/download" className="seo-btn seo-btn--primary">Download CheckScript</Link>
              <Link to="/contact" className="seo-btn seo-btn--secondary">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>
    </SEOPageLayout>
  );
}
